import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Aperture, CreditCard, Building2, Mail, Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useSiteContent } from "@/context/SiteContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const PLANS = [
  {
    id: "starter",
    ckey: "starter",
    priceKey: "starter_price",
    featureIcon: Aperture,
    cta: "Suscribirme",
    ctaIcon: CreditCard,
    type: "checkout",
    highlight: false,
  },
  {
    id: "studio_pro",
    ckey: "studio_pro",
    priceKey: "studio_pro_price",
    featureIcon: Aperture,
    cta: "Suscribirme",
    ctaIcon: CreditCard,
    type: "checkout",
    highlight: true,
  },
  {
    id: "studio_advanced",
    ckey: "advanced",
    priceKey: "advanced_price",
    featureIcon: Building2,
    cta: "Consultar precio",
    ctaIcon: Mail,
    type: "contact",
    highlight: false,
  },
];

export const Pricing = () => {
  const { t } = useSiteContent();
  const [loadingId, setLoadingId] = useState(null);

  const handleCheckout = async (plan) => {
    if (plan.type === "contact") {
      scrollTo("contact");
      return;
    }
    setLoadingId(plan.id);
    try {
      const { data } = await axios.post(`${API}/payments/checkout/session`, {
        plan_id: plan.id,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      toast.error("No se pudo iniciar el pago. Inténtalo de nuevo.");
      setLoadingId(null);
    }
  };

  return (
    <section id="pricing" className="relative py-28 px-6" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="text-sm uppercase tracking-[0.2em] text-indigo-400 font-medium">{t('pricing_eyebrow')}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {t('pricing_title')} <span className="text-gradient">{t('pricing_highlight')}</span>
          </h2>
          <p className="mt-4 text-white/55 text-lg">
            {t('pricing_subtitle')}
          </p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.id}
              id={`plan-${p.id}`}
              data-testid={`pricing-card-${p.id}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative rounded-3xl p-8 flex flex-col scroll-mt-28 ${
                p.highlight
                  ? "border border-indigo-400/40 bg-gradient-to-b from-indigo-500/[0.08] to-violet-500/[0.04] shadow-[0_8px_40px_rgba(99,102,241,0.18)]"
                  : "glass"
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-xs font-semibold">
                  Más popular
                </span>
              )}

              <div className="flex items-start justify-between gap-4">
                <h3 className="text-2xl font-semibold">{t(`${p.ckey}_name`)}</h3>
                <div className="text-right shrink-0">
                  {(() => {
                    const raw = (t(p.priceKey) || "").trim();
                    const isNum = /^\d+([.,]\d+)?$/.test(raw);
                    return (
                      <>
                        <span className="text-3xl font-bold text-gradient">{isNum ? `${raw}€` : raw}</span>
                        {isNum && <span className="text-white/50 text-sm">/mes</span>}
                      </>
                    );
                  })()}
                </div>
              </div>

              <p className="mt-3 text-white/55 leading-relaxed">{t(`${p.ckey}_desc`)}</p>

              <div className="mt-6 flex items-center gap-3 text-white/85">
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/5 shrink-0">
                  <p.featureIcon className="h-5 w-5 text-indigo-400" />
                </span>
                <span className="font-medium">{t(`${p.ckey}_feature`)}</span>
              </div>

              <Button
                data-testid={`pricing-cta-${p.id}`}
                onClick={() => handleCheckout(p)}
                disabled={loadingId === p.id}
                className={`mt-8 h-12 rounded-full gap-2 text-base border-0 ${
                  p.highlight
                    ? "bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white shadow-[0_8px_30px_rgba(99,102,241,0.35)]"
                    : "bg-white text-black hover:bg-white/90"
                }`}
              >
                {loadingId === p.id ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <><p.ctaIcon className="h-5 w-5" /> {p.cta}</>
                )}
              </Button>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-center text-white/40 text-sm">
          Pago seguro con Stripe · El pago con PayPal estará disponible próximamente.
        </p>
      </div>
    </section>
  );
};
